import React, { useCallback, useMemo } from 'react';
import { Tooltip } from '@primer/react';
import { max } from 'date-fns';
import parseISO from 'date-fns/parseISO';

import AlternatingRowDataTable from '../../../common/DataTable/AlternatingRowDataTable';
import Button from '../../../common/Button/Button';
import Icon from '../../../common/icons/Icon';
import Label from '../../../common/Labels/Label';
import LinkLabel from '../../../common/Labels/LinkLabel';

import PlayIcon from '../../../common/icons/PlayIcon';
import StopIcon from '../../../common/icons/StopIcon';
import TrashFillIcon from '../../../common/icons/TrashFillIcon';
import VersionIcon from '../../../common/icons/VersionIcon';

import PipelineStatusWithPopover from '../../../pipelines/PipelineStatusBadge/PipelineStatusWithPopover';

import { formatDateWithTimeSeparator } from '../../../../utils/date_utils';
import { getThemeColor } from '../../../../utils/theme-utils';
import { preserveWorkspaceURL } from '../../../../utils/url_utils';

const transformPipelinesToTableRows = (pipelines = []) => pipelines?.map((group) => {
  const {
    pipeline, pipeline_version, deployment, workspace 
  } = group;

  const pipeline_updated_at = pipeline?.updated_at && parseISO(pipeline.updated_at);
  const version_updated_at = pipeline_version?.updated_at && parseISO(pipeline_version.updated_at);
  const deployment_updated_at = deployment?.updated_at && parseISO(deployment.updated_at);

  const modelSteps = pipeline_version?.steps?.filter(val => val.ModelInference !== undefined) ?? [];
  const numModelSteps = modelSteps?.length || 0;
  const numSteps = pipeline_version?.steps?.length || 0;

  const deploymentOrVersion = deployment?.created_by || pipeline_version?.created_by;

  return ({
    pipelineID: pipeline.id,
    id: pipeline.id,
    name: pipeline.name,
    deployed: deployment?.deployed,
    deployment_updated_at: deployment?.updated_at,
    deployment,
    pipeline_version,
    workspaceName: workspace.name,
    workspace: {
      workspace_id: workspace.id,
      name: workspace.name,
      created_by: workspace.created_by
    },
    // Only one version is returned- the most recent one. If it's deployed
    status: deployment?.deployed ? 'Active' : 'Inactive',
    created_at: pipeline.created_at,
    created_by: pipeline.created_by,
    updated_at: max([pipeline_updated_at, version_updated_at, deployment_updated_at].filter(x => !!x)),
    updated_by: deployment_updated_at > pipeline_updated_at ? deploymentOrVersion : pipeline_version?.created_by,
    last_version: pipeline_version?.version,
    // FIXME: This should be returned on the version object.
    last_version_update: pipeline.updated_at,
    steps: pipeline_version?.steps?.filter(val => val.ModelInference !== undefined)?.length ?? 0,
    numSteps,
    numModelSteps,
    // last_version_update: pipeline_version.created_at,
  })
})

const truncateText = (inputText, truncateAt = 8) => {
  if (inputText.length < truncateAt + 1) {
    return inputText;
  }
  return `${inputText.substring(0, truncateAt)}...`;
}

const CellSubLabel = ({ children, ...rest }) => (
  <Label className="mt-1 break-words" style={{ color: getThemeColor('info') }} {...rest} >{ children }</Label>
)

const NameCell = ({ row, value }) => {
  // Right side of || operator - old logic, preserved as a fallback
  const latestVersion = row?.original?.last_version || row?.original?.pipeline_version?.last_version;
  const truncatedName = truncateText(value || '--', 30);

  return (
    <div className="flex flex-col p-2">
      <LinkLabel
        className="break-words"
        to={preserveWorkspaceURL(`/pipelines/${value}/versions/${latestVersion}`)}
        size="lg"
        title={value}
      >
        {truncatedName}
      </LinkLabel>
    </div>
  )
}

const WorkspaceCell = ({ value, row }) => {
  const workspace = row?.original?.workspace;
  const truncatedName = truncateText(value || '--', 30);
  const createdBy = workspace?.created_by;
  const truncatedOwner = truncateText(createdBy || '--', 20);
  return (
    <div className="flex flex-col">
      <Label className="break-words">{truncatedName}</Label>
      {
        createdBy?.length > 0 && (
          <CellSubLabel>{truncatedOwner}</CellSubLabel>
        )
      }
    </div>
  )
}

const StepsCell = ({ value, row }) => {
  const modelSteps = row?.original?.numModelSteps;
  return (
    <div className="flex flex-col">
      <Label>{value}</Label>
      {
        modelSteps > 0 && (
          <CellSubLabel>{modelSteps} model{modelSteps > 1 ? 's' : ''}</CellSubLabel>
        )
      }
    </div>
  )
}

// TODO - Need to check pipeline versions for this value, not the pipeline object
const StatusCell = ({ row, value }) => <PipelineStatusWithPopover deploymentUpdatedAt={row?.original?.deployment_updated_at} deployed={row?.original?.deployed} status={value} />

const CreatedCell = ({ value }) => {
  if (value === null || value === undefined) {
    return <Label>--</Label>
  }
  return (
    <Label>{formatDateWithTimeSeparator(new Date(value).toISOString())}</Label>
  )
}

// Updated At from a Product perspective includes:
// 1) when the Pipeline object changes
// 2) when a new Pipeline Version is created
// 3) when an associated Deployment object is updated
const UpdatedAtCell = ({ value, row }) => {
  if (value === null || value === undefined) {
    return <Label>--</Label>
  }
  const updatedBy = row?.original?.updated_by;
  const truncatedUpdatedBy = truncateText(updatedBy || '--', 20);
  return (
    <div className="flex flex-col">
      <Label>{formatDateWithTimeSeparator(value.toISOString())}</Label>
      {
        updatedBy && (
          <CellSubLabel>{truncatedUpdatedBy}</CellSubLabel>
        )
      }
    </div>
  )
}

const DeployedAtCell = ({ value, row }) => {
  if (value === null || value === undefined) {
    return <Label>--</Label>
  }
  const deployedBy = row?.original?.deployment?.updated_by;
  const truncatedDeployedBy = truncateText(deployedBy || '--', 20);
  return (
    <div className="flex flex-col">
      <Label>{formatDateWithTimeSeparator(new Date(value).toISOString())}</Label>
      {
        !!deployedBy?.length && (
          <CellSubLabel>{truncatedDeployedBy}</CellSubLabel>
        )
      }
    </div>
  )
}

const ActionCellRenderer = ({
  row, meta = {},
}) => {
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  }

  // const pipelineId = row?.original?.id;
  // const workspaceId = row?.original?.workspace?.workspace_id;
  const isActive = row?.original?.deployed;
  const FirstButtonIcon = isActive ? StopIcon : PlayIcon;

  const onFirstButtonClick = useCallback((e) => {
    meta?.showPipelineDeploymentModal(row);
    e.stopPropagation()
  }, [meta, row])

  const onSecondButtonClick = useCallback((e) => {
    meta?.viewPipelineHistory(row)
    e.stopPropagation()
  }, [meta, row])

  const onThirdButtonClick = useCallback((e) => {
    meta?.removePipeline(row?.original)
    e.stopPropagation()
  }, [meta, row])

  return (
    <div className="flex flex-row justify-between items-center w-16">
      <Tooltip aria-label={isActive ? 'Undeploy pipeline' : 'Deploy pipeline'}>
        <Button className="mr-1" sx={buttonStyle} onClick={onFirstButtonClick} >
          <Icon
            icon={FirstButtonIcon}
            width={24}
            height={24}
            fill={getThemeColor('brand')}
          />
        </Button>
      </Tooltip>
      <Tooltip aria-label="Manage pipeline versions">
        <Button className="mr-1" sx={buttonStyle} onClick={onSecondButtonClick} >
          <Icon
            icon={VersionIcon}
            fill={getThemeColor('brand')}
            size={24}
            useTailwindSizing
          />
        </Button>
      </Tooltip>
      <Tooltip direction="nw" aria-label={isActive ? 'Unable to delete deployed pipeline' : 'Delete pipeline'}>
        <Button sx={buttonStyle} onClick={onThirdButtonClick} disabled={isActive}>
          <Icon
            icon={TrashFillIcon}
            width={24}
            height={20}
            fill={getThemeColor(isActive ? 'info' : 'brand')}
          />
        </Button>
      </Tooltip>
    </div>
  )
}

export const PipelineAdminTable = ({
  pipelines = [],
  removePipeline = () => {},
  viewPipelineHistory = () => {},
  showPipelineDeploymentModal = () => {},
  onPipelineClick = () => {},
  selectedPage = 1,
  resultsPerPage = 25,
}) => {
  const columns = useMemo(() => [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: NameCell,
      width: 200
    },
    {
      Header: 'Workspace',
      accessor: 'workspaceName',
      Cell: WorkspaceCell,
      width: 180
    },
    {
      Header: 'Created',
      accessor: 'created_at',
      Cell: CreatedCell,
      width: 140,
    },
    {
      Header: 'Last Updated',
      accessor: 'updated_at',
      id: 'Updated',
      Cell: UpdatedAtCell,
      width: 140,
    },
    {
      Header: 'Last Deployed',
      accessor: 'deployment_updated_at',
      id: 'Deployed',
      Cell: DeployedAtCell,
      width: 140,
    },
    {
      Header: 'Steps',
      accessor: 'numSteps',
      Cell: StepsCell,
      width: 64,
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: StatusCell,
      width: 80,
    },
    {
      Header: 'Actions',
      Cell: ActionCellRenderer,
      width: 64,
      disableSortBy: true,
    }
  ], []);

  const defaultSort = {
    id: 'name',
    desc: false
  }

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )
  const meta = React.useMemo(() => ({
    showPipelineDeploymentModal, viewPipelineHistory, removePipeline,
  }), [removePipeline, showPipelineDeploymentModal, viewPipelineHistory])

  const tableRows = transformPipelinesToTableRows(pipelines);

  return (
    <AlternatingRowDataTable
      id="pipeline-admin-table"
      columns={columns}
      data={tableRows}
      defaultColumn={defaultColumn}
      defaultSort={defaultSort}
      meta={meta}
      onItemClick={onPipelineClick}
      height={640}
      pagination={{
        resultsPerPage,
        selectedPage
      }}
    />
  )
};

export default PipelineAdminTable;
